import { Component, OnInit, ViewChild, Input, OnDestroy, AfterViewInit, HostBinding, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpEventType, HttpEvent } from '@angular/common/http';
import { MatSelect } from '@angular/material/select';
import { Subscription, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import HTTP_STATUS_CODES from '../../enums/HttpStatusCodesEnum';

import { DocumentModel } from '../../models/DocumentModel';
import { HttpService } from 'src/app/services/http.service';
import { MetaFileLink } from 'src/app/models/metaFileLink.model';
import { AppSettingsService } from 'src/app/services/app-settings.service';

export interface UploaderDocumentType {
  value: number; // value of enums for each type
  text: string;
  hint: string;
}

export type FileExtension = '.pdf' | '.png' | '.jpg' | '.jpeg' | '.gif' | '.csv' | '.xlsx';

@Component({
  selector: 'terra-file-uploader',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],

  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileUploadComponent,
      multi: true
    }
  ]
})
export class FileUploadComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  changeFn: (DocumentModel) => {};
  touchedFn: () => {};

  private subs = {
    documentTypeChanged: null as Subscription
  };
  private UPLOAD_FILE_ENDPOINT = 'upload';
  private compressableTypes = ['jpg', 'jpeg', 'png'];

  // This property is used from components that contain an uploader,
  // to know if the anything changed inside the uplodaer (to determine if a step should be submitted for example)
  public isChanged = false;

  public uploadEndpoint = this.appSettings.resourceUrl + 'upload';

  documentTypeControl = new UntypedFormControl();

  // tslint:disable-next-line:variable-name
  public _documentTypes: UploaderDocumentType[];

  public typeHint = '';

  get documentTypes() {
    return this._documentTypes;
  }

  @ViewChild(MatSelect)
  ddlTypes: MatSelect;

  @Input()
  isSubmitted:boolean = false;

  @Input()
  isCompressable:boolean = false;

  @Input()
  uploadButtonText:string = '+ Upload';

  @Input()
  supportedExtensions: FileExtension[];

  @Input()
  required:boolean = true;

  @Input()
  fileTypeNotSupported:boolean = false;

  @Input()
  isErrorSetFromOutside:boolean = false;

  get withTypeSelect() {
    return this._documentTypes.length > 0;
  }

  get isShowUnselectedTypeError() {
    return this.documentTypeControl.hasError('required') && (this.documentTypeControl.dirty || this.isSubmitted);
  }

  get isShowMissingFileError() {
    return this.required && this.isSubmitted && !this.document.id;
  }

  // tslint:disable-next-line:adjacent-overload-signatures
  @Input()
  set documentTypes(items: UploaderDocumentType[]) {
    this._documentTypes = items;
    if (items.length === 1) {
      this.document.documentType = items[0].value;
      this.typeHint = items[0].hint;
    }
    // Set the validators of the file type select
    if (items.length > 1) {
      this.documentTypeControl.setValidators(Validators.required);
    } else {
      this.documentTypeControl.setValidators(null);
    }
  }

  @HostBinding('class.read-only-mode') readOnlyMode = false;

  public document = new MetaFileLink();
  public isDocumentSelected = false;
  public showFileUploadSuccess = false;
  public showFileUploadError = false;
  public showFileLoader = false;
  public displayVirusDetectedError = false;
  uploadProgress = 0;

  constructor(private http: HttpService, private appSettings: AppSettingsService, private changeDetectorRef: ChangeDetectorRef) {

  }

  ngOnInit() { }

  dropDownToggleClick() {
    this.touchedFn();
    this.ddlTypes.toggle();
  }

  ngAfterViewInit(): void {
    this.subscribeToDocumentTypeChanged();
  }

  // When there is more than 1 option for document type, each selection from the drop down list should emit the change to the outside.
  subscribeToDocumentTypeChanged() {
    this.subs.documentTypeChanged = this.documentTypeControl.valueChanges.subscribe(documentTypeOption => {
      this.isChanged = true;
      this.document.documentType = documentTypeOption ? documentTypeOption.value : null;
      this.typeHint = documentTypeOption?.hint || '';
      this.changeFn(this.document);
    });
  }

  clearFile() {
    if (this.readOnlyMode) {
      return;
    }
    this.touchedFn();
    this.isChanged = true;
    // Clear the file but leave the selected file type unchanged
    this.isDocumentSelected = false;
    this.isSubmitted = true;
    this.document.id = null;
    this.document.title = null;
    this.document.url = null;
    this.document.sizeB = null;
    this.document.mediaType = null;
    this.showFileUploadError = false;
    this.fileTypeNotSupported = false;
    this.displayVirusDetectedError = false;
    this.changeFn(null);
  }

  fileChange(event) {
    if (this.readOnlyMode) {
      return;
    }
    this.isChanged = true;
    this.showFileLoader = false;
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      const fileToUpload: File = fileList[0];
      // Fill the file name so it can be displyaed while uploading
      this.document.title = fileToUpload.name;

      this.showFileLoader = true;
      this.fileTypeNotSupported = false;
      this.showFileUploadError = false;
      this.displayVirusDetectedError = false;
      this.uploadProgress = 0;

      const fileType = fileToUpload.name.substr(fileToUpload.name.lastIndexOf('.') + 1);
      let uploadObservable: Observable<HttpEvent<{}>>;

      if (this.compressableTypes.includes(fileType.toLowerCase()) && this.isCompressable) {
        uploadObservable = this.http.compressImage(fileToUpload).pipe(
          switchMap(compressedFile => {
            return this.http.uploadFile(this.UPLOAD_FILE_ENDPOINT, compressedFile);
          })
        );
      } else {
        uploadObservable = this.http.uploadFile(this.UPLOAD_FILE_ENDPOINT, fileToUpload);
      }

      uploadObservable.pipe(
        tap(result => {
          if (result.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round((100 * result.loaded) / result.total);
          } else if (result.type === HttpEventType.Response) {
            this.showFileLoader = false;
            const file = result.body as MetaFileLink;
            if (file.id > 0) {
              // The selected document type from the Drop down
              file.documentType = this.document.documentType;
              // Trigger the file uploaded event only if the file type from the drop down is also selectd.
              // When there is no drop down (only a single option), the: "file.documentType" will already be populated.
              if (!this.documentTypes || (file.documentType !== undefined && file.documentType !== null)) {
                this.changeFn(file);
              }
              this.document = file;
              this.isDocumentSelected = true;
            } else {
              this.showFileUploadError = true;
            }
          }
        })
      ).subscribe(
        result => {
          this.changeDetectorRef.detectChanges();
        },
        // Error
        error => {
          this.displayErrorMessage(error.status);
        }
      );
    }
  }

  setValue(file: MetaFileLink) {
    if (file) {
      this.document = { ...this.document, ...file } as MetaFileLink;
      if (file.documentType) {
        this.documentTypeControl.setValue(this.documentTypes.find(d => d.value === this.document.documentType));
      }
      this.isDocumentSelected = true;
    }
  }

  writeValue(model: MetaFileLink): void {
    this.setValue(model);
  }

  registerOnChange(fn: any): void {
    this.changeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.touchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.readOnlyMode = isDisabled;
    if (isDisabled) {
      this.documentTypeControl.disable();
    } else {
      this.documentTypeControl.enable();
    }
  }

  getSupportedFileTypes() {
    if (!this.supportedExtensions || this.supportedExtensions.length === 0) {
      return '.pdf, .png, .jpg, .jpeg';
    } else {
      const typesString = this.supportedExtensions.join(', ');
      return typesString;
    }
  }

  ngOnDestroy(): void {
    if (this.subs.documentTypeChanged) {
      this.subs.documentTypeChanged.unsubscribe();
    }
  }

  displayErrorMessage(httpError: HTTP_STATUS_CODES) {
    switch (httpError) {
      case HTTP_STATUS_CODES.BAD_REQUEST:
        this.displayVirusDetectedError = true;
        break;
      case HTTP_STATUS_CODES.UNSUPPORTED_MEDIA_TYPE:
        this.fileTypeNotSupported = true;
        break;
      default:
        this.showFileUploadError = true;
        break;
    }

    this.showFileLoader = false;
    this.changeDetectorRef.detectChanges();
  }
}
